import React from 'react';
import { Loader2, CheckCircle2, Circle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AIPipelineStatus, AIPipelineStage } from '@/types/ai-health-companion-types';

interface PipelineStageIndicatorProps {
  status: AIPipelineStatus;
}

const STAGE_LABELS: Record<AIPipelineStage, string> = {
  understanding: 'Understanding',
  'safety-screening': 'Safety Check',
  'context-loading': 'Loading Context',
  analyzing: 'Analyzing',
  generating: 'Generating',
  citing: 'Citing Sources',
  'hypothesis-generation': 'Hypothesis Generation',
  'adversarial-review': 'Adversarial Review',
  'checklist-verification': 'Checklist Verification',
  consensus: 'Forming Consensus',
  'response-generation': 'Response Generation',
  complete: 'Complete',
};

const STAGE_ORDER: AIPipelineStage[] = [
  'understanding',
  'safety-screening',
  'context-loading',
  'analyzing',
  'generating',
  'citing',
  'complete',
];

const PipelineStageIndicator: React.FC<PipelineStageIndicatorProps> = ({ status }) => {
  const { currentStage, stagesCompleted, estimatedTimeRemaining } = status;

  const getStageStatus = (stage: AIPipelineStage): 'active' | 'complete' | 'pending' => {
    if (stage === currentStage) return 'active';
    if (stagesCompleted.includes(stage)) return 'complete';
    return 'pending';
  };

  const renderStageIcon = (stage: AIPipelineStage) => {
    const stageStatus = getStageStatus(stage);

    if (stageStatus === 'active') {
      return (
        <div className="relative">
          <Loader2 className="w-4 h-4 text-primary-600 dark:text-primary-400 animate-spin" />
          <div className="absolute inset-0 w-4 h-4 rounded-full bg-primary-400 dark:bg-primary-500 opacity-20 animate-ping" />
        </div>
      );
    }

    if (stageStatus === 'complete') {
      return <CheckCircle2 className="w-4 h-4 text-green-500" />;
    }

    return <Circle className="w-4 h-4 text-gray-300 dark:text-gray-600" />;
  };

  const renderConnectorLine = (index: number) => {
    if (index === STAGE_ORDER.length - 1) return null;

    const currentStageStatus = getStageStatus(STAGE_ORDER[index]);
    const isCompleted = currentStageStatus === 'complete';

    return (
      <div
        className={cn(
          'absolute left-[7px] top-6 w-0.5 h-4',
          isCompleted
            ? 'bg-green-500'
            : 'bg-gray-200 dark:bg-gray-700'
        )}
      />
    );
  };

  return (
    <div className="py-2">
      {/* Header */}
      <div className="flex items-center justify-between mb-3 px-1">
        <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
          Processing your request
        </p>
        {estimatedTimeRemaining && (
          <p className="text-xs text-gray-500 dark:text-gray-500">
            ~{estimatedTimeRemaining}s
          </p>
        )}
      </div>

      {/* Stages */}
      <div className="space-y-0">
        {STAGE_ORDER.map((stage, index) => {
          const stageStatus = getStageStatus(stage);
          const label = STAGE_LABELS[stage];

          return (
            <div key={stage} className="relative">
              <div className="flex items-center gap-2.5 py-1.5">
                {/* Icon */}
                <div className="flex-shrink-0">{renderStageIcon(stage)}</div>

                {/* Label */}
                <p
                  className={cn(
                    'text-sm transition-colors',
                    stageStatus === 'active' &&
                      'text-primary-700 dark:text-primary-300 font-medium',
                    stageStatus === 'complete' &&
                      'text-gray-600 dark:text-gray-400',
                    stageStatus === 'pending' &&
                      'text-gray-400 dark:text-gray-600'
                  )}
                >
                  {label}
                </p>

                {/* Pulse animation for active stage */}
                {stageStatus === 'active' && (
                  <div className="ml-auto">
                    <div className="w-2 h-2 rounded-full bg-primary-500 animate-pulse" />
                  </div>
                )}
              </div>

              {/* Connector Line */}
              {renderConnectorLine(index)}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default React.memo(PipelineStageIndicator);
